import ErrorManager from '@/common/utils/ErrorManager';
import { EquipementItems } from '@/models/EquipementItems';
import { EtatEquipement } from '@/models/EtatEquipement';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState } from '@/plugins/store';
import { ActionTree, Commit, MutationTree } from 'vuex';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import { EQUIPEMENT } from '@/modules/Intervention/store/mutation-types';
import { getters } from '@/modules/Intervention/store/modules/equipement-getters';
import { SortType, sortValue } from '@/models/SortType';
import { TableHeader } from '@/models/TableHeader';
import { EquipementState } from '@/modules/Intervention/store/modules/Equipement';
import i18n from '@/plugins/i18n';

export const preavisEquipementState = (): EquipementState => ({
    equipement: [],
    etatsEquipement: [],
    interventions: [],
    isLoading: false,
    error: false,
    equipementFilter: '',
    groupeFilter: '',
    ssGroupeFilter: '',
    isEtatLoading: false,
    isLoadingInterventions: false,
    errorEtat: false,
    errorInterventions: false,
    page: 0,
    numberOfPages: 0,
    scroll: 0,
    tableHeader: [
        {
            label: i18n.global.t('equipementSelection.table.equipement'),
            sortData: 'equipementLibelle',
            sortStatus: SortType.ASC,
            width: '32%',
        },
        {
            label: i18n.global.t('equipementSelection.table.groupe'),
            sortData: 'groupeLibelle',
            sortStatus: SortType.NONE,
            width: '32%',
        },
        {
            label: i18n.global.t('equipementSelection.table.sousgroupe'),
            sortData: 'sousGroupeLibelle',
            sortStatus: SortType.NONE,
            width: '32%',
        },
    ],
    sort: {
        sortData: 'equipementLibelle',
        sortStatus: SortType.ASC,
    },
});

export enum PreavisEquipementActions {
    GET_EQUIPEMENT = 'GET_EQUIPEMENT',
    GET_ETAT_EQUIPEMENT = 'GET_ETAT_EQUIPEMENT',
}

interface PreavisEquipementActionType {
    commit: Commit;
    state: EquipementState;
}

const actions: ActionTree<EquipementState, RootState> = {
    async [PreavisEquipementActions.GET_EQUIPEMENT](
        { commit, rootState }: any,
        payload: {
            adresseSiteId: number;
            page: number;
            equipementFilter: string;
            groupeFilter: string;
            ssGroupeFilter: string;
            sort: { sortData: string; sortStatus: SortType };
        }
    ): Promise<void> {
        commit(EQUIPEMENT.SET_PAGE, payload.page);
        commit(EQUIPEMENT.SET_LOADING, true);
        return EquipementService.getEquipement(
            payload.adresseSiteId,
            payload.equipementFilter,
            payload.groupeFilter,
            payload.ssGroupeFilter,
            sortValue(payload.sort.sortData, payload.sort.sortStatus) || '',
            (payload?.page ?? 0).toString()
        )
            .then(async response => {
                // Vérifier que le site preavis n'a pas changé entre-temps
                const currentSiteId = rootState?.preavisInterventionState?.preavisSite?.id;
                if (currentSiteId && currentSiteId !== payload.adresseSiteId) {
                    return;
                }
                if (response !== undefined || response.status !== undefined) {
                    if (response.status !== 200) {
                        commit(EQUIPEMENT.SET_ERROR, true);
                        commit(EQUIPEMENT.SET_LOADING, false);
                        commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                            { root: true }
                        );
                    }
                    const equipementData: EquipementItems = response.data;
                    if (payload.page === 0) {
                        commit(EQUIPEMENT.SET_SCROLL, 0);
                        commit(EQUIPEMENT.SET_ITEMS, equipementData);
                    } else {
                        commit(EQUIPEMENT.ADD_ITEMS, equipementData);
                    }
                    commit(EQUIPEMENT.SET_NUMBER_OF_PAGES, parseInt(response.headers.pages) || 0);
                    commit(EQUIPEMENT.SET_LOADING, false);
                } else {
                    commit(EQUIPEMENT.SET_ERROR, true);
                    commit(EQUIPEMENT.SET_LOADING, false);
                    commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                        { root: true }
                    );
                }
            })
            .catch(async error => {
                commit(EQUIPEMENT.SET_ERROR, true);
                commit(EQUIPEMENT.SET_LOADING, false);
                commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            });
    },

    async [PreavisEquipementActions.GET_ETAT_EQUIPEMENT]({ commit }: PreavisEquipementActionType): Promise<void> {
        commit(EQUIPEMENT.SET_ETAT_LOADING, true);
        EquipementService.getEtatEquipement()
            .then(async response => {
                if (response !== undefined || response.status !== undefined) {
                    if (response.status !== 200) {
                        commit(EQUIPEMENT.SET_ETAT_ERROR, true);
                        commit(EQUIPEMENT.SET_ETAT_LOADING, false);
                        commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                            { root: true }
                        );
                    }
                    commit(EQUIPEMENT.SET_ETAT_ITEMS, response.data);
                    commit(EQUIPEMENT.SET_ETAT_LOADING, false);
                } else {
                    commit(EQUIPEMENT.SET_ETAT_ERROR, true);
                    commit(EQUIPEMENT.SET_ETAT_LOADING, false);
                    commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                        { root: true }
                    );
                }
            })
            .catch(async error => {
                commit(EQUIPEMENT.SET_ETAT_ERROR, true);
                commit(EQUIPEMENT.SET_ETAT_LOADING, false);
                commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            });
    },
};

const mutations: MutationTree<EquipementState> = {
    [EQUIPEMENT.SET_LOADING](state: EquipementState, isLoading: boolean): void {
        state.isLoading = isLoading;
    },
    [EQUIPEMENT.SET_ERROR](state: EquipementState, error: boolean): void {
        state.error = error;
    },
    [EQUIPEMENT.SET_ETAT_ITEMS](state: EquipementState, etatsEquipememnt: EtatEquipement[]) {
        state.etatsEquipement = etatsEquipememnt;
    },
    [EQUIPEMENT.SET_ETAT_LOADING](state: EquipementState, isLoading: boolean): void {
        state.isEtatLoading = isLoading;
    },
    [EQUIPEMENT.SET_SCROLL](state: EquipementState, scroll: number): void {
        state.scroll = scroll;
    },
    [EQUIPEMENT.SET_ETAT_ERROR](state: EquipementState, error: boolean): void {
        state.errorEtat = error;
    },
    [EQUIPEMENT.SET_INTERVENTIONS_LOADING](state: EquipementState, isLoading: boolean): void {
        state.isLoadingInterventions = isLoading;
    },
    [EQUIPEMENT.SET_INTERVENTIONS_ERROR](state: EquipementState, error: boolean): void {
        state.errorInterventions = error;
    },
    [EQUIPEMENT.SET_INTERVENTIONS](state: EquipementState, interventions: any[]): void {
        state.interventions = interventions;
    },
    [EQUIPEMENT.ADD_ITEMS](state: EquipementState, items: EquipementItems[]): void {
        state.equipement = [...state.equipement, ...items];
    },
    [EQUIPEMENT.SET_ITEMS](state: EquipementState, items: EquipementItems[]): void {
        state.equipement = items;
    },
    [EQUIPEMENT.SET_EQUIPEMENT_FILTER](state: EquipementState, filter: string): void {
        state.equipementFilter = filter;
    },
    [EQUIPEMENT.SET_GROUPE_FILTER](state: EquipementState, filter: string): void {
        state.groupeFilter = filter;
    },
    [EQUIPEMENT.SET_SS_GROUPE_FILTER](state: EquipementState, filter: string): void {
        state.ssGroupeFilter = filter;
    },
    [EQUIPEMENT.SET_PAGE](state: EquipementState, page: number): void {
        state.page = page;
    },
    [EQUIPEMENT.SET_NUMBER_OF_PAGES](state: EquipementState, numberOfPages: number): void {
        state.numberOfPages = numberOfPages;
    },
    [EQUIPEMENT.SET_SORT](
        state,
        sort: {
            sortData: string;
            sortStatus: SortType;
        }
    ) {
        const tableHeader = state.tableHeader.reduce((newTable, headerItem) => {
            if (headerItem.sortData !== sort.sortData) {
                if (headerItem.sortStatus !== SortType.INACTIVE) headerItem.sortStatus = SortType.NONE;
            } else headerItem.sortStatus = sort.sortStatus;
            return [...newTable, headerItem];
        }, [] as TableHeader[]);
        state.tableHeader = tableHeader;
        state.sort = sort;
    },
};

const preavisEquipement = {
    namespaced: true,
    state: preavisEquipementState,
    actions,
    mutations,
    getters,
};

export default preavisEquipement;
